import lubavLoader from '@backrs_ui/common-ui/src/assets/animations/dots-dotting.json';
import { appColors } from '@backrs_ui/common-ui/src/assets/appColors';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { BackrsIcon } from '@backrs_ui/common-ui/src/atoms/BackrsIcon';
import { AskLubavButtonEndpoint } from '@backrs_ui/common-ui/src/models/nugget';
import { BackrsUser } from '@backrs_ui/common-ui/src/models/user';

import { Box, Button, SxProps, Theme, Typography, Tooltip } from '@mui/material';
import Lottie from 'lottie-react';

import { ButtonConfig } from './AskLubavButtonGroup';

type AskLubavButtonProps = Readonly<{
  apiKey: AskLubavButtonEndpoint;
  config: ButtonConfig;
  isSelected: boolean;
  setIsSelected: (isSelected: boolean) => void;
  isLoading: boolean;
  isAnyLoading: boolean;
  user: BackrsUser | undefined;
  enableAllOptions?: boolean;
  onButtonClick: (apiKey: AskLubavButtonEndpoint) => void;
}>;

export const AskLubavButton = ({
  apiKey,
  config,
  isSelected,
  setIsSelected,
  isLoading,
  isAnyLoading,
  user,
  enableAllOptions = false,
  onButtonClick,
}: AskLubavButtonProps) => {
  const { isDisabled, disabledReason } = getButtonState(config, user, enableAllOptions);

  const handleClick = () => {
    if (isLoading || isAnyLoading) {
      return;
    }

    if (!isSelected) {
      onButtonClick(apiKey);
    }

    setIsSelected(!isSelected);
  };

  const ButtonComponent = (
    <Box sx={{ position: 'relative' }}>
      <Button
        onClick={handleClick}
        disabled={isDisabled}
        sx={{
          ...buttonStyle,
          backgroundColor: isDisabled ? `${appColors.neutral.black}16` : appColors.color.blue10,
          border: `3px solid ${isSelected ? appColors.color.blue1 : 'transparent'}`,
          cursor: isLoading || isAnyLoading ? 'default' : 'pointer',
        }}
      >
        <Box sx={iconStyle}>
          <BackrsIcon
            name="IconPlusBlue"
            alt="plus sign"
            style={{
              ...plusSignStyle,
              opacity: isDisabled ? 0.7 : 1,
            }}
          />
        </Box>
        <Typography
          sx={{
            ...textStyles,
            color: isDisabled ? `${appColors.neutral.black}70` : appColors.color.blue1,
          }}
        >
          {config.buttonTitle}
        </Typography>
        {isLoading && (
          <Box sx={loaderBoxStyle}>
            <Lottie animationData={lubavLoader} loop style={animationStyle} />
          </Box>
        )}
      </Button>
    </Box>
  );

  return disabledReason ? (
    <Tooltip
      title={disabledReason}
      arrow
      componentsProps={{
        popper: {
          sx: {
            [`&.MuiTooltip-popper`]: {
              zIndex: 99999,
            },
          },
        },
      }}
    >
      {ButtonComponent}
    </Tooltip>
  ) : (
    ButtonComponent
  );
};

const getButtonState = (
  config: ButtonConfig,
  user: BackrsUser | undefined,
  enableAllOptions: boolean,
): { isDisabled: boolean; disabledReason: string | null } => {
  if (enableAllOptions) {
    return { isDisabled: false, disabledReason: null };
  }

  if (config.disabledInProd) {
    return {
      isDisabled: true,
      disabledReason: appStrings.modules.nugget.askLubav.errors.COMING_SOON,
    };
  }

  if (config.requirements) {
    if (config.requirements.requireUser && !user) {
      return {
        isDisabled: true,
        disabledReason: appStrings.modules.nugget.askLubav.errors.NOT_LOGGED_IN,
      };
    }

    if (config.requirements.requiresResume && !user?.hasDocument?.resume) {
      return {
        isDisabled: true,
        disabledReason: `${appStrings.modules.nugget.askLubav.errors.RESUME_REQUIRED}${appStrings.modules.nugget.askLubav.backrsProfile}!`,
      };
    }
  }

  return { isDisabled: false, disabledReason: null };
};

const buttonStyle: SxProps<Theme> = {
  borderRadius: '9px',
  px: 2,
  py: 1,
  position: 'relative',
  paddingLeft: '32px',
} as const;

const textStyles: SxProps<Theme> = {
  fontSize: '16px',
  textTransform: 'none',
  fontWeight: 700,
} as const;

const loaderBoxStyle: SxProps<Theme> = {
  height: '24px',
  width: '24px',
  marginLeft: 1,
} as const;

const animationStyle: React.CSSProperties = {
  height: '100%',
  width: '100%',
} as const;

const iconStyle: SxProps<Theme> = {
  position: 'absolute',
  left: '8px',
  display: 'flex',
  alignItems: 'center',
} as const;

const plusSignStyle: React.CSSProperties = {
  height: 14,
  width: 14,
} as const;
